import { EditorView, ViewPlugin, type ViewUpdate } from '@codemirror/view';
import {
  getSearchQuery,
  searchPanelOpen,
  setSearchQuery,
} from '@codemirror/search';

// Counting is O(document), so stop once the number stops being useful. A
// common term in a large payload can otherwise match tens of thousands of
// times on every keystroke.
const MATCH_LIMIT = 5000;

interface MatchCount {
  total: number;
  current: number;
  capped: boolean;
}

function countMatches(view: EditorView): MatchCount | null {
  const query = getSearchQuery(view.state);
  // Empty query, or a regexp that is still half-typed.
  if (!query.valid) return null;

  const selection = view.state.selection.main;
  let total = 0;
  let current = 0;
  try {
    const cursor = query.getCursor(view.state);
    for (let next = cursor.next(); !next.done; next = cursor.next()) {
      total += 1;
      const { from, to } = next.value;
      if (from === selection.from && to === selection.to) current = total;
      if (total >= MATCH_LIMIT) break;
    }
  } catch {
    return null;
  }
  return { total, current, capped: total >= MATCH_LIMIT };
}

function label({ total, current, capped }: MatchCount): string {
  if (total === 0) return 'No results';
  const of = capped ? `${MATCH_LIMIT}+` : `${total}`;
  // Before the first jump nothing is selected yet, so report the total only.
  if (current === 0) return `${of} ${total === 1 ? 'match' : 'matches'}`;
  return `${current} of ${of}`;
}

/**
 * CodeMirror's search panel reports no match count. This appends one next to
 * the search field and keeps it in step with the query and the selection.
 */
export const searchMatchCount = ViewPlugin.fromClass(
  class {
    private node: HTMLElement | null = null;

    constructor(view: EditorView) {
      this.sync(view);
    }

    update(update: ViewUpdate) {
      if (!searchPanelOpen(update.state)) {
        this.detach();
        return;
      }
      const queryChanged = update.transactions.some((transaction) =>
        transaction.effects.some((effect) => effect.is(setSearchQuery))
      );
      if (
        !this.node ||
        queryChanged ||
        update.docChanged ||
        update.selectionSet
      ) {
        this.sync(update.view);
      }
    }

    destroy() {
      this.detach();
    }

    private detach() {
      this.node?.remove();
      this.node = null;
    }

    private sync(view: EditorView) {
      const panel = view.dom.querySelector('.cm-panel.cm-search');
      if (!panel) {
        this.detach();
        return;
      }
      if (!this.node || !panel.contains(this.node)) {
        this.node = document.createElement('span');
        this.node.className = 'cm-search-count';
        this.node.setAttribute('aria-live', 'polite');
        const field = panel.querySelector('input[name="search"]');
        if (field) field.after(this.node);
        else panel.append(this.node);
      }
      const result = countMatches(view);
      this.node.textContent = result ? label(result) : '';
    }
  }
);
